import { IPosIdsConfiguration, IPriceDto } from 'generated/graphql-gateway';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useDerivedServiceModeContext } from 'state/service-mode/derived';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { getEntryPrice } from 'state/unified-menu/utils';
import { IWithVendorConfig, findVendorConfig } from 'utils/vendor-config';

import { useDebugToolPosVendor } from './use-debug-tool-pos-vendor';

export type IDebugPosIdsConfiguration = Pick<
  IPosIdsConfiguration,
  'combo' | 'container' | 'deliveryPlu' | 'plu' | 'injectionMode' | 'injectionType'
>;

export interface IDebugMenuEntity extends IWithVendorConfig {
  _id: string;
  sanityId?: string;
  posIdsConfiguration?: IDebugPosIdsConfiguration | null;
}

const getOneMenuEntryId = (entity: IDebugMenuEntity, entryId?: string) =>
  entryId ?? (entity.sanityId?.trim() ? entity.sanityId : entity._id);

export const getDebugSanityDocumentId = (entityId: string) => {
  const idParts = entityId.split('$').filter(Boolean);
  return idParts[idParts.length - 1] ?? entityId;
};

export const useDebugMenuItemData = ({
  entity,
  entryId,
  contextualPrice,
}: {
  entity: IDebugMenuEntity;
  entryId?: string;
  contextualPrice?: IPriceDto;
}) => {
  const isOneMenu = useEnableUnifiedMenu();
  const { storePosVendor } = useDebugToolPosVendor();
  const { indexedMenu, isLoading: isOneMenuLoading } = useUnifiedMenuContext();
  const { posServiceMode } = useDerivedServiceModeContext();

  const oneMenuEntryId = getOneMenuEntryId(entity, entryId);
  const sanityDocumentId = getDebugSanityDocumentId(entity._id);

  const menuEntry = isOneMenu ? indexedMenu.find(oneMenuEntryId) : undefined;
  const price = menuEntry ? getEntryPrice(menuEntry, posServiceMode, contextualPrice) : undefined;
  const vendorConfig = !isOneMenu
    ? findVendorConfig(entity.vendorConfigs, storePosVendor)
    : undefined;

  return {
    isOneMenu,
    oneMenuEntryId,
    sanityDocumentId,
    vendorConfig,
    posIdsConfiguration: entity.posIdsConfiguration ?? undefined,
    price,
    priceStatus: isOneMenuLoading
      ? ('loading' as const)
      : !menuEntry
      ? ('entry-not-found' as const)
      : price === undefined
      ? ('price-not-configured' as const)
      : ('available' as const),
  };
};
